import { createElement, useEffect, useLayoutEffect, useRef, type JSX } from "react";
import type { NotificationLevel } from "@tc-superpods/app-contract";
import type { TcToaster } from "@tc-superpods/components/tc/types";
import type { TcToastVariant } from "@tc-superpods/components/tc/types";
import type { NotificationToast } from "../types";

export interface NotificationRegionProps {
  notifications: NotificationToast[];
  onDismiss: (id: string) => void;
}

export function toToastVariant(level: NotificationLevel): TcToastVariant {
  if (level === "error") {
    return "danger";
  }

  return level;
}

export function NotificationRegion({
  notifications,
  onDismiss
}: NotificationRegionProps): JSX.Element {
  const toasterRef = useRef<TcToaster | null>(null);

  useLayoutEffect(() => {
    const toaster = toasterRef.current;
    if (!toaster) {
      return;
    }

    toaster.entries = notifications.map((notification) => ({
      id: notification.id,
      message: notification.body,
      title: notification.title,
      variant: toToastVariant(notification.level)
    }));
  }, [notifications]);

  useEffect(() => {
    const toaster = toasterRef.current;
    if (!toaster) {
      return;
    }

    const handleDismiss = (event: Event): void => {
      const toastElement = event
        .composedPath()
        .find(
          (element): element is HTMLElement =>
            element instanceof HTMLElement && element.tagName === "TC-TOAST"
        );
      const id = toastElement?.dataset.toastId;
      if (id) {
        onDismiss(id);
      }
    };

    toaster.addEventListener("tc-dismiss", handleDismiss);

    return () => {
      toaster.removeEventListener("tc-dismiss", handleDismiss);
    };
  }, [onDismiss]);

  return createElement("tc-toaster", {
    "data-platform-notification-region": "",
    placement: "bottom-end",
    ref: toasterRef
  });
}
